import React from 'react';
import PropTypes from 'prop-types';
import {
  Link,
  Card,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';
import { Receipt as ReceiptIcon } from '@material-ui/icons';
import { formatCurrency } from 'src/utils/currency';
import { formatDateISOWithSeconds } from 'src/utils/date';
import { getRetailPaymentStatusFriendlyName } from 'src/utils/retailPaymentStatusTypes';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
    '& .MuiTableCell-root': {
      border: 0,
    },
    '& .MuiTableRow-root': {
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
    },
  },
  fontWeightMedium: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  cardHeader: {
    color: theme.palette.secondary.main,
  },
}));

function RetailPaymentsInfo({
  paymentDate,
  paymentAmount,
  paymentStatus,
  refundedAmount,
  retailPaymentId,
  stripeInvoiceId,
  transactionId,
  routeToRetailPaymentDetail,
}) {
  const classes = useStyles();

  if (!retailPaymentId && !paymentAmount) {
    return (
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="No Retail Payment. Retail Payment info not available"
        />
      </Card>
    );
  }

  const renderRow = (label, value) => (
    <TableRow>
      <TableCell className={classes.fontWeightMedium}>{label}</TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="textSecondary">
          {value || '-'}
        </Typography>
      </TableCell>
    </TableRow>
  );

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.cardHeader}
        title="Motorist Retail Payments"
        action={
          <Button
            variant="contained"
            color="secondary"
            size="small"
            disabled={!retailPaymentId}
            startIcon={<ReceiptIcon />}
            component={RouterLink}
            to={routeToRetailPaymentDetail}
          >
            View Details
          </Button>
        }
      />
      <Divider />
      <Table>
        <TableBody>
          {renderRow('Payment Amount', paymentAmount ? formatCurrency(paymentAmount) : null)}
          {paymentDate &&
            renderRow('Payment Date', formatDateISOWithSeconds(paymentDate))}
          {paymentStatus &&
            renderRow('Payment Status', getRetailPaymentStatusFriendlyName(paymentStatus))}
          {refundedAmount > 0 &&
            renderRow('Refunded Amount', formatCurrency(refundedAmount))}
          {transactionId &&
            renderRow('Retail Transaction ID', transactionId)}
          {stripeInvoiceId && (
            <TableRow>
              <TableCell className={classes.fontWeightMedium}>Link to Stripe Invoice</TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="textSecondary">
                  <Link
                    target="_blank"
                    href={`https://dashboard.stripe.com/invoices/${stripeInvoiceId}`}
                    rel="noreferrer"
                  >
                    Open
                  </Link>
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

RetailPaymentsInfo.propTypes = {
  paymentDate: PropTypes.string,
  paymentAmount: PropTypes.number,
  paymentStatus: PropTypes.string,
  refundedAmount: PropTypes.number,
  retailPaymentId: PropTypes.number,
  stripeInvoiceId: PropTypes.number,
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  routeToRetailPaymentDetail: PropTypes.string,
};

export default RetailPaymentsInfo;